import React, { useMemo } from "react";
import { DataTable as Table, Badge, Button, Avatar, TableStyles, useRowHighlight } from "@/components/ui";
import { Phone, UserX, UserPlus, Eye, Edit2, Clock } from "lucide-react";
import { TableActions } from "../../../components/ui/TableActions";
import toast from "react-hot-toast";
import {
  ADMIN_TABLE_HEADERS,
  TELECALLER_TABLE_HEADERS,
  CONTACT_STAGES
} from "@/constants/globalConstants";
import { getMemberName, formatDate } from "../utils/customerTableHelpers";
import { TABLE_BACKGROUNDS } from "@/constants/tableBackgrounds";

/**
 * CustomersTableView
 * Main table component for displaying customers.
 */


/* Developer : Nithin
Date : 24-04-2026
changes: Renamed the table headings */

export const CustomersTableView = ({
  filtered,
  updateStatus,
  onCustomerClick,
  stages,
  onEdit,
  onToggleDisable,
  onAddActivity,
  user,
  customHeaders = null,
  maxHeight = "max-h-[600px]",
  isLoading = false,
  extraActions = null,
  teamMembers = [],
  onAssignDesigner,
  onUpdateProgressStatus,
}) => {
  const isAdmin = ["admin", "owner", "verve owner", "verve-admin", "systemadmin"].includes(user?.role?.toLowerCase()?.trim()) || (user?.role || "").toLowerCase().includes("owner") || (user?.role || "").toLowerCase().includes("admin");

  const designers = (teamMembers || []).filter(u => {
    const uRole = (u.role || "").toLowerCase().trim().replace(/[-_\s]+/g, "-");
    return uRole === 'designer';
  });

  // Memoize watch keys to prevent unnecessary re-renders
  const watchKeys = useMemo(() => ['status', 'teleStatus'], []);
  const { getHighlightClass } = useRowHighlight(filtered, '_id', 5000, '!bg-amber-100', watchKeys);

  const handleCopyPhone = (phone) => {
    if (!phone) {
      toast.error("No phone number available!");
      return;
    }
    const fallbackCopy = (text) => {
      const textArea = document.createElement("textarea");
      textArea.value = text;
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      try {
        document.execCommand('copy');
        toast.success("Number copied: " + text);
      } catch (err) {
        toast.error("Failed to copy number.");
      }
      document.body.removeChild(textArea);
    };

    if (navigator.clipboard && window.isSecureContext) {
      navigator.clipboard.writeText(phone).then(() => {
        toast.success("Number copied: " + phone);
      }).catch(() => {
        fallbackCopy(phone);
      });
    } else {
      fallbackCopy(phone);
    }
  };

  const getStageDisplay = (status) => {
    const stage = stages?.find(s => s.key === status) || CONTACT_STAGES?.find(s => s.key === status);
    if (!stage) return { label: status || 'New', colorClass: 'bg-slate-100 text-slate-500 border-slate-200' };
    return {
      label: stage.label,
      colorClass: `${stage.color.replace('bg-', 'text-').replace('500', '700')} bg-white border-${stage.color.replace('bg-', '')}`
    };
  };

  const getSiteExecutiveName = (l) => {
    if (!l.siteExecutive) return "—";
    if (typeof l.siteExecutive === 'object') {
      return getMemberName(l.siteExecutive) || "—";
    }
    if (typeof l.siteExecutive === 'string' && teamMembers && Array.isArray(teamMembers)) {
      const found = teamMembers.find(t => t._id === l.siteExecutive);
      if (found) {
        return (found.name || `${found.firstName || ""} ${found.lastName || ""}`.trim()) || l.siteExecutive;
      }
    }
    return l.siteExecutive;
  };

  const columns = (() => {
    const baseCols = [
      {
        header: "Verve ID",
        key: "customId",
        minWidth: '130px',
        render: (l) => (
          <div className="flex flex-col gap-1 items-start">
            <span className={TableStyles.idCell}>
              {l.customId || "—"}
            </span>
            {l.status === 'appointment_scheduled' && l.siteExecutive && (l.siteExecutive._id === user?._id || l.siteExecutive === user?._id) && (
              <div className="flex items-center gap-1 text-amber-500 font-bold text-[8px] animate-pulse uppercase tracking-wider bg-amber-50 px-1 py-[1px] rounded border border-amber-200" title="Ready For Site Visit">
                <div className="w-1 h-1 rounded-full bg-amber-500 animate-ping shrink-0"></div>
                <span className="whitespace-nowrap">Site Visit</span>
              </div>
            )}
          </div>
        )

      },
      {
        header: "Customer Info",
        key: "customerName",
        render: (l) => {
          const fullName = l.customerName || `${l.firstName || ""} ${l.lastName || ""}`.trim() || "No Name";

          const isReminderDue = l.isReminderDue;

          return (
            <div className="flex items-center gap-2.5 min-w-0">
              <div className="flex flex-col min-w-0">
                <span className={`${TableStyles.primaryText} flex items-center gap-2 flex-wrap`}>
                  {fullName}
                  {isReminderDue && (
                    <div className="flex items-center gap-1 text-red-500 font-bold text-[9px] animate-pulse uppercase tracking-wider">
                      <Clock size={10} />
                      <span>Follow Up Due</span>
                    </div>
                  )}
                </span>
                <span className={`${TableStyles.secondaryText} flex items-center gap-1`}>
                  {l.phoneNumber || l.phone || "No Phone"}
                  {l.email && <span className="mx-1 opacity-50">|</span>}
                  {l.email && l.email.toLowerCase()}
                </span>
              </div>
            </div>
          );
        }
      },
      {
        header: "Address",
        key: "location",
        render: (l) => {
          const addressParts = [
            l.addressLine1,
            l.addressLine2,
            l.city,
            l.district,
            l.state,
            l.pincode
          ].filter(Boolean);
          const fullAddress = addressParts.length > 0 ? addressParts.join(', ') : "—";

          return (
            <div className="flex flex-col">
              <span
                className={`${TableStyles.regularText} max-w-[200px] line-clamp-2`}
                title={fullAddress}
              >
                {fullAddress}
              </span>
            </div>
          );
        }
      },
      {
        header: "Source",
        key: "source",
        minWidth: "100px",
        render: (l) => {
          const sourceLabel = l.source === 'Bulk' ? 'Bulk Import' : (l.source || "Manual");
          return (
            <span className={TableStyles.badge}>
              {sourceLabel}
            </span>
          );
        }
      },
      {
        header: "Reference Type",
        key: "referenceType",
        minWidth: "120px",
        render: (l) => {
          return (
            <span className={`${TableStyles.regularText} capitalize`}>
              {l.referenceType || "—"}
            </span>
          );
        }
      }
    ];

    if (isAdmin) {
      baseCols.push({
        header: "Staff Name",
        key: "assignedTo",
        minWidth: "130px",
        render: (l) => {
          const assignee = l.assignedTo || l.telecaller;
          return assignee ? (
            <div className="flex flex-col min-w-0">
              <div className={`${TableStyles.primaryText} truncate`} title={getMemberName(assignee)}>
                {getMemberName(assignee)}
              </div>
              <div className={`${TableStyles.secondaryText} whitespace-nowrap`}>
                {formatDate(l.updatedAt || l.assignedAt)}
              </div>
            </div>
          ) : (
            <span className="text-[13px] text-slate-400">Unassigned</span>
          );
        }
      });

      baseCols.push({
        header: "Site Exec",
        key: "siteExecutive",
        minWidth: "130px",
        render: (l) => {
          const seName = getSiteExecutiveName(l);
          const dateVal = l.siteExecutiveAssignedAt || l.appointmentDate || l.updatedAt || l.createdAt;
          return seName !== "—" ? (
            <div className="flex flex-col min-w-0">
              <div className={`${TableStyles.primaryText} truncate`} title={seName}>
                {seName}
              </div>
              <div className={`${TableStyles.secondaryText} whitespace-nowrap`}>
                {formatDate(dateVal)}
              </div>
            </div>
          ) : (
            <span className="text-[13px] text-slate-400">—</span>
          );
        }
      });

      baseCols.push({
        header: "Designer",
        key: "designer",
        minWidth: "120px",
        render: (l) => {
          const currentDesignerId = l.designer?._id || l.designer || "";
          const isAssigned = designers.some((u) => u._id === currentDesignerId);

          return (
            <div className="w-[140px]" onClick={(e) => e.stopPropagation()}>
              <select
                value={isAssigned ? currentDesignerId : ""}
                disabled={isAssigned || l.status !== 'initial_design'} // Locked once assigned, or if not in initial_design stage
                onChange={(e) => onAssignDesigner && onAssignDesigner(l._id, e.target.value)}
                className={`w-full text-[11px] font-normal bg-white border border-slate-200 rounded-md px-2 py-1 outline-none transition-all text-slate-700 ${(isAssigned || l.status !== 'initial_design') ? "opacity-70 bg-slate-50 cursor-not-allowed" : "cursor-pointer hover:border-brand-blue focus:ring-1 focus:ring-brand-blue/20"}`}
              >
                {!isAssigned && <option value="" disabled>Select Designer</option>}
                {designers.map((u) => (
                  <option key={u._id} value={u._id}>
                    {u.name || `${u.firstName || ''} ${u.lastName || ''}`.trim() || 'Unknown'}
                  </option>
                ))}
              </select>
            </div>
          );
        }
      });

      baseCols.push({
        header: "Initial Quote",
        key: "initialQuoteMaker",
        minWidth: "130px",
        render: (l) => {
          const maker = l.initialQuoteMaker || (l.initialQuotationData && l.initialQuotationData.createdBy);
          const dateVal = l.initialQuoteDate || (l.initialQuotationData && (l.initialQuotationData.updatedAt || l.initialQuotationData.createdAt));
          const makerName = maker ? getMemberName(maker) : (l.initialQuoteMakerName || (l.initialQuotationData && l.initialQuotationData.creatorName) || null);
          return makerName ? (
            <div className="flex flex-col min-w-0">
              <div className={`${TableStyles.primaryText} truncate capitalize`} title={makerName}>
                {makerName}
              </div>
              <div className={`${TableStyles.secondaryText} whitespace-nowrap`}>
                {formatDate(dateVal)}
              </div>
            </div>
          ) : (
            <span className="text-[13px] text-slate-400">—</span>
          );
        }
      });

      baseCols.push({
        header: "Status",
        key: "teleStatus",
        minWidth: "140px",
        render: (l) => {
          const displayStatus = l.status === "drop" ? "drop" : l.teleStatus;
          const { label, colorClass } = getStageDisplay(displayStatus);
          return (
            <span className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[11px] font-normal border whitespace-nowrap ${colorClass}`}>
              <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${colorClass.includes('text-emerald') ? 'bg-emerald-500' : 'bg-slate-400'}`} />
              {label}
            </span>
          );
        }
      });



      baseCols.push({
        header: "Created Date",
        key: "createdAt",
        minWidth: "100px",
        render: (l) => (
          <span className="text-[11px] text-slate-500 whitespace-nowrap">
            {l.createdAt ? new Date(l.createdAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : "—"}
          </span>
        )
      });

      /* baseCols.push({
        header: "Updated Date",
        key: "updatedAt",
        minWidth: "100px",
        render: (l) => (
          <span className="text-[11px] text-slate-500 whitespace-nowrap">
            {l.updatedAt ? new Date(l.updatedAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : "—"}
          </span>
        )
      }); */

      baseCols.push({
        header: "App Schedule Date",
        key: "appointmentDate",
        minWidth: "120px",
        render: (l) => {
          const dateStr = l.appointmentDate ? new Date(l.appointmentDate).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : "—";
          return (
            <span className="text-[11px] text-slate-500 truncate block max-w-[120px]" title={dateStr}>
              {dateStr}
            </span>
          );
        }
      });

      baseCols.push({
        header: "Next Stage",
        key: "nextStage",
        render: (l) => {
          const displayStatus = l.status === "drop" ? "drop" : (l.teleStatus || l.status || "");
          const isDropped = displayStatus === "drop";
          const isAppointed = !isDropped && (l.status === "converted" || l.teleStatus === "converted" || l.isConvertedByTelecaller);
          const isFullyLocked = l.isDisabled || ["project_started", "project_start"].includes(l.status);

          let availableStages = stages?.filter(s => s.key !== 'new' && s.key !== 'assigned') || [];

          const isValidSelection = availableStages.some(s => s.key === displayStatus);
          const selectValue = isValidSelection ? displayStatus : "";

          return (
            <div className="text-center" onClick={(e) => e.stopPropagation()}>
              <select
                value={selectValue}
                onChange={(e) => updateStatus(l, e.target.value)}
                className="text-[10px] uppercase text-brand-dark bg-white border border-slate-200 rounded px-2 py-1 cursor-pointer hover:border-brand-blue transition-colors focus:outline-none focus:ring-1 focus:ring-brand-blue"
                disabled={isFullyLocked}
              >
                <option value="" disabled>Select Status</option>
                {availableStages.map(s => (
                  <option key={s.key} value={s.key}>{s.label}</option>
                ))}
              </select>
            </div>
          );
        }
      });

      baseCols.push({
        header: "Progress Status",
        key: "progressStatus",
        className: "text-center w-36",
        render: (l) => {
          const currentProg = (l.progressStatus === "Initially Not Started" || !l.progressStatus) ? "Not Started" : l.progressStatus;
          return (
            <div className="text-center" onClick={(e) => e.stopPropagation()}>
              <select
                value={currentProg}
                onChange={(e) => {
                  if (onUpdateProgressStatus) {
                    onUpdateProgressStatus(l._id, e.target.value);
                  }
                }}
                className="text-[10px] uppercase text-brand-dark bg-white border border-slate-200 rounded px-2 py-1 cursor-pointer hover:border-brand-blue transition-colors focus:outline-none focus:ring-1 focus:ring-brand-blue"
              >
                <option value="Not Started">Not Started</option>
                <option value="In Progress">In Progress</option>
                <option value="Completed">Completed</option>
              </select>
            </div>
          );
        }
      });

      baseCols.push({
        header: "Notes",
        key: "lastActivityNote",
        className: "min-w-[250px]",
        render: (l) => {
          let lastNote = l.lastActivityNote || l.notes || "No notes yet";
          if (lastNote && typeof lastNote === 'string' && lastNote.includes(' | Note: ')) {
            lastNote = lastNote.split(' | Note: ')[1];
          }
          return (
            <div
              className={`${TableStyles.noteText} truncate max-w-[250px] inline-block`}
              title={lastNote}
            >
              {lastNote}
            </div>
          );
        }
      });

      baseCols.push({
        header: "Actions",
        key: "actions",
        className: "text-center w-32",
        render: (l) => {
          const isDisabled = l.isDisabled;
          const isSiteVisit = l.teleStatus === "converted" || l.status === "converted";
          const isSalesProcessing = l.isConvertedByTelecaller && !["new", "assigned", "converted", "site_visit"].includes(l.status);
          /* Developer: Nithin | Date: 09-07-2026 | Changes: Enable works after disable; View & Edit hidden when contact is disabled */
          const isStatusLocked = ["project_started", "project_start"].includes(l.status) || (!isSiteVisit && isSalesProcessing);

          return (
            <div onClick={(e) => e.stopPropagation()} className="flex items-center justify-start gap-1 w-[120px] mx-auto pl-1">
              <TableActions
                onView={(!isDisabled && onCustomerClick) ? () => onCustomerClick(l) : undefined}
                onEdit={(!isDisabled && onEdit) ? () => onEdit(l) : undefined}
                onToggle={onToggleDisable ? () => onToggleDisable(l._id, !!l.isDisabled) : undefined}
                isActive={!l.isDisabled}
                disabled={isStatusLocked}
              />
              {extraActions && extraActions(l)}
            </div>
          );
        }
      });
    } else {
      // Telecaller / Client Coordinator specific columns
      const isClientCoord = ["client_coordinator", "client coordinator"].includes((user?.role || "").toLowerCase().trim());
      if (isClientCoord) {
        baseCols.push({
          header: "Site Exec",
          key: "siteExecutive",
          minWidth: "130px",
          render: (l) => {
            const seName = getSiteExecutiveName(l);
            const dateVal = l.siteExecutiveAssignedAt || l.appointmentDate || l.updatedAt || l.createdAt;
            return seName !== "—" ? (
              <div className="flex flex-col min-w-0">
                <div className={`${TableStyles.primaryText} truncate`} title={seName}>
                  {seName}
                </div>
                <div className={`${TableStyles.secondaryText} whitespace-nowrap`}>
                  {formatDate(dateVal)}
                </div>
              </div>
            ) : (
              <span className="text-[13px] text-slate-400">—</span>
            );
          }
        });
      }

      const roleStrCheck = (user?.role || "").toLowerCase().trim();
      const isDesignerRoleCheck = roleStrCheck === "designer" || roleStrCheck.includes("designer");
      const isClientCoordCheck = ["client_coordinator", "client coordinator", "telecaller", "staff"].includes(roleStrCheck) || roleStrCheck.includes("client");
      if (isDesignerRoleCheck || isClientCoordCheck || isAdmin) {
        baseCols.push({
          header: "Initial Quote",
          key: "initialQuoteMaker",
          minWidth: "130px",
          render: (l) => {
            const maker = l.initialQuoteMaker || (l.initialQuotationData && l.initialQuotationData.createdBy);
            const dateVal = l.initialQuoteDate || (l.initialQuotationData && (l.initialQuotationData.updatedAt || l.initialQuotationData.createdAt));
            const makerName = maker ? getMemberName(maker) : (l.initialQuoteMakerName || (l.initialQuotationData && l.initialQuotationData.creatorName) || null);
            return makerName ? (
              <div className="flex flex-col min-w-0">
                <div className={`${TableStyles.primaryText} truncate capitalize`} title={makerName}>
                  {makerName}
                </div>
                <div className={`${TableStyles.secondaryText} whitespace-nowrap`}>
                  {formatDate(dateVal)}
                </div>
              </div>
            ) : (
              <span className="text-[13px] text-slate-400">—</span>
            );
          }
        });
      }

      baseCols.push({
        header: "Stage",
        key: "teleStatus",
        minWidth: "120px",
        render: (l) => {
          const displayStatus = l.status === "drop" ? "drop" : l.teleStatus;
          const { label, colorClass } = getStageDisplay(displayStatus);
          return (
            <span className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[11px] font-normal border uppercase tracking-tighter whitespace-nowrap ${colorClass}`}>
              <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${colorClass.includes('text-emerald') ? 'bg-emerald-500' : 'bg-slate-400'}`} />
              {label}
            </span>
          );
        }
      });





      baseCols.push({
        header: "Created Date",
        key: "createdAt",
        minWidth: "100px",
        render: (l) => (
          <span className="text-[11px] text-slate-500 whitespace-nowrap">
            {l.createdAt ? new Date(l.createdAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : "—"}
          </span>
        )
      });

      /* baseCols.push({
        header: "Updated Date",
        key: "updatedAt",
        minWidth: "100px",
        render: (l) => (
          <span className="text-[11px] text-slate-500 whitespace-nowrap">
            {l.updatedAt ? new Date(l.updatedAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : "—"}
          </span>
        )
      }); */

      baseCols.push({
        header: "App Schedule Date",
        key: "appointmentDate",
        minWidth: "120px",
        render: (l) => {
          const dateStr = l.appointmentDate ? new Date(l.appointmentDate).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : "—";
          return (
            <span className="text-[11px] text-slate-500 truncate block max-w-[120px]" title={dateStr}>
              {dateStr}
            </span>
          );
        }
      });

      const roleStr = (user?.role || "").toLowerCase().trim();
      const isDesignerRole = roleStr === "designer" || roleStr.includes("designer");
      const isClientCoordRole = ["client_coordinator", "client coordinator", "telecaller", "staff"].includes(roleStr) || roleStr.includes("client");
      const canEditProgressStatus = isDesignerRole || isAdmin;
      const showProgressStatus = isDesignerRole || isAdmin || isClientCoordRole;

      if (!isDesignerRole) {
        baseCols.push({
          header: "Next Stage",
          key: "nextStage",
          render: (l) => {
            // Use l.status (same as Stage badge) as the selected value so dropdown always matches the badge
            const displayStatus = l.status === "drop" ? "drop" : (l.status || l.teleStatus || "");
            const isDropped = displayStatus === "drop";
            const isAppointed = !isDropped && (l.status === "converted" || l.teleStatus === "converted" || l.isConvertedByTelecaller);
            const isFullyLocked = l.isDisabled || ["project_started", "project_start"].includes(l.status);

            const isFieldMarketing = ["field_marketing", "field marketing"].includes(roleStr);

            let availableStages = stages?.filter(s => s.key !== 'new' && s.key !== 'assigned') || [];
            if (isFieldMarketing) {
              const allowedKeys = ["appointment_scheduled", "site_visit", "drop"];
              // Always include the current status so the select value has a matching option
              if (displayStatus && !allowedKeys.includes(displayStatus)) {
                allowedKeys.unshift(displayStatus);
              }
              availableStages = (stages || CONTACT_STAGES).filter(s => allowedKeys.includes(s.key));
              // If current status still not found in filtered stages, add a manual option fallback
              const hasCurrentOption = availableStages.some(s => s.key === displayStatus);
              if (!hasCurrentOption && displayStatus) {
                availableStages = [
                  { key: displayStatus, label: displayStatus.replace(/_/g, ' ') },
                  ...availableStages
                ];
              }
            } else if (!isClientCoordRole && isAppointed) {
              availableStages = availableStages.filter(s => s.key === 'converted' || s.key === 'drop');
            }

            return (
              <div className="text-center" onClick={(e) => e.stopPropagation()}>
                <select
                  value={displayStatus}
                  onChange={(e) => updateStatus(l, e.target.value)}
                  className="text-[10px] uppercase text-brand-dark bg-white border border-slate-200 rounded px-2 py-1 cursor-pointer hover:border-brand-blue transition-colors focus:outline-none focus:ring-1 focus:ring-brand-blue"
                  disabled={isFullyLocked}
                >
                  <option value="" disabled>Select Status</option>
                  {availableStages.map(s => (
                    <option key={s.key} value={s.key}>{s.label}</option>
                  ))}
                </select>
              </div>
            );
          }
        });
      }

      if (showProgressStatus) {
        baseCols.push({
          header: "Progress Status",
          key: "progressStatus",
          className: "text-center w-36",
          render: (l) => {
            const currentProg = (l.progressStatus === "Initially Not Started" || !l.progressStatus) ? "Not Started" : l.progressStatus;
            return (
              <div className="text-center" onClick={(e) => e.stopPropagation()}>
                <select
                  value={currentProg}
                  onChange={(e) => {
                    if (onUpdateProgressStatus && canEditProgressStatus) {
                      onUpdateProgressStatus(l._id, e.target.value);
                    }
                  }}
                  disabled={!canEditProgressStatus}
                  className={`text-[10px] uppercase text-brand-dark bg-white border border-slate-200 rounded px-2 py-1 transition-colors focus:outline-none focus:ring-1 focus:ring-brand-blue ${
                    canEditProgressStatus ? "cursor-pointer hover:border-brand-blue" : "cursor-not-allowed opacity-80 bg-gray-50"
                  }`}
                >
                  <option value="Not Started">Not Started</option>
                  <option value="In Progress">In Progress</option>
                  <option value="Completed">Completed</option>
                </select>
              </div>
            );
          }
        });
      }

      baseCols.push({
        header: "Last Note",
        key: "lastCallNote",
        className: "min-w-[250px]",
        render: (l) => {
          const activities = l.callHistory || l.activities || [];
          const lastNoteObj = activities.length > 0 ? activities[0] : null;
          let lastNote = lastNoteObj ? (lastNoteObj.note || lastNoteObj.comment || lastNoteObj.notes) : (l.notes || l.lastCallNote || "No notes yet");

          if (lastNote && typeof lastNote === 'string' && lastNote.includes(' | Note: ')) {
            lastNote = lastNote.split(' | Note: ')[1];
          }

          return (
            <span className="text-[11px] text-slate-600 block leading-normal py-1 italic">
              {lastNote}
            </span>
          );
        }
      });

      baseCols.push({
        header: "Actions",
        key: "actions",
        className: "text-center w-32",
        render: (l) => {
          const isDisabled = l.isDisabled;
          const isSiteVisit = l.teleStatus === "converted" || l.status === "converted";
          const isSalesProcessing = l.isConvertedByTelecaller && !["new", "assigned", "converted", "site_visit"].includes(l.status);
          /* Developer: Nithin | Date: 09-07-2026 | Changes: Enable works after disable; View & Edit hidden when contact is disabled */
          const isStatusLocked = ["project_started", "project_start"].includes(l.status) || (!isSiteVisit && isSalesProcessing);
          const roleStr = (user?.role || "").toLowerCase().trim();
          const isFieldMarketing = ["field_marketing", "field marketing"].includes(roleStr);
          const isDesigner = roleStr === "designer" || roleStr.includes("designer");

          return (
            <div onClick={(e) => e.stopPropagation()} className="flex items-center justify-start gap-1 w-[120px] mx-auto pl-1">
              <TableActions
                onView={(!isDisabled && onCustomerClick) ? () => onCustomerClick(l) : undefined}
                onEdit={(!isDisabled && !isFieldMarketing && onEdit) ? () => onEdit(l) : undefined}
                onToggle={(!isFieldMarketing && !isDesigner && onToggleDisable) ? () => onToggleDisable(l._id, !!l.isDisabled) : undefined}
                isActive={!l.isDisabled}
                disabled={isStatusLocked}
              />
              {extraActions && extraActions(l)}
            </div>
          );
        }
      });
    }

    return baseCols;
  })();

  return (
    <Table
      columns={columns}
      data={filtered}
      backgroundImage={TABLE_BACKGROUNDS.contacts}
      isLoading={isLoading}
      /* onRowClick={(idx, row) => onCustomerClick(row)} */
      isRowInactive={(row) => !!row?.isDisabled}
      getRowClassName={(row) => {
        let baseClass = '';
        if (row?.isDisabled) baseClass = 'bg-slate-100/90 border-y border-slate-200/80';
        else if (row.isReminderDue) baseClass = 'bg-red-50/50 hover:bg-red-50 transition-colors duration-500 animate-[pulse_3s_ease-in-out_infinite]';
        else baseClass = 'hover:bg-slate-50/50';

        return getHighlightClass(row, baseClass);
      }}
      getRowTitle={(row) => {
        return row?.isReminderDue ? "Follow-Up Reminder Due" : undefined;
      }}
      maxHeight={maxHeight}
      tableClassName="min-w-full"
    />
  );
};
